import { KIND_APPEARANCE } from '../constants';
import type { ActivityKind, KindAppearance } from '../types';

import type { KindFilter, StatusFilter } from './types';

/**
 * The filter's options, each carrying the appearance it is drawn with.
 *
 * The kinds come from `KIND_APPEARANCE`, which is where a kind is declared — a list written out here
 * would be a second declaration of the same set, and the one that silently falls behind. Only the
 * relationship nothing declares is written: `all` leads, and wears no appearance, because it is the
 * way back out of the other three.
 */
export const KIND_FILTERS: { value: KindFilter; label: string; appearance?: KindAppearance }[] = [
  { value: 'all', label: 'All' },
  ...Object.entries(KIND_APPEARANCE).map(([kind, appearance]) => ({
    value: kind as ActivityKind,
    label: appearance.label,
    appearance,
  })),
];

export const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'needsAttention', label: 'Needs attention' },
  { value: 'settled', label: 'Settled' },
];

/**
 * Three empty states, because they mean three different things and a single "Nothing to show"
 * would make the best of them read like the worst.
 *
 * `nothingWaiting` is the **good** outcome and must not read like a filter that went wrong: Clive
 * has raised things and every one of them is settled. `nothingRaised` is a claim Clive has not
 * raised anything on yet, which is not the same fact and must not borrow the good outcome's words.
 * It says *raised*, not "nothing from Clive": the log beside it can already hold Clive's narration,
 * and only a recommendation, a request or a question is something this panel would list.
 */
export const EMPTY_STATE = {
  nothingRaised: "Clive hasn't raised anything yet.",
  nothingWaiting: 'Nothing needs your attention. Everything Clive has raised is settled.',
  noMatch: 'Nothing matches this filter.',
};
